import { useEffect } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { LogOut, Monitor, Moon, Sun, Zap } from 'lucide-react'
import api, { getCurrentUser } from '../../services/api.js'
import { useAuthStore } from '../../stores/authStore.js'
import { useThemeStore } from '../../stores/themeStore.js'
import { useCredits } from '../../hooks/useCredits.js'
import { NAV_ITEMS, navItemFor } from './navigation.js'
import BrandLogo from '../common/BrandLogo.jsx'

const THEMES = [
  { value: 'system', label: 'Auto', Icon: Monitor },
  { value: 'light', label: 'Clair', Icon: Sun },
  { value: 'dark', label: 'Sombre', Icon: Moon },
]

function roleLabel(user) {
  if (user?.roles?.includes('ROLE_ADMIN')) return 'Direction'
  if (user?.organizationRole === 'owner') return 'Responsable'
  return 'Commercial'
}

/** Pastilles du menu : actions du jour (en retard + aujourd'hui) et projets auto nouveaux. */
function useNavBadges(user, showAutoProjects) {
  const { data: day } = useQuery({
    queryKey: ['myDay'],
    queryFn: async () => (await api.get('/api/me/day')).data,
    enabled: Boolean(user),
    staleTime: 60_000,
  })
  const { data: autoProjects } = useQuery({
    queryKey: ['autoProjects', 'navCounts'],
    queryFn: async () => (await api.get('/api/crm/auto-projects', { params: { itemsPerPage: 1 } })).data,
    enabled: Boolean(user) && showAutoProjects,
    staleTime: 60_000,
  })
  return {
    jour: (day?.counts?.overdue ?? 0) + (day?.counts?.today ?? 0),
    newAutoProjects: autoProjects?.counts?.newCount ?? 0,
  }
}

/** Barre latérale « espace commercial » (maquette Jean Louis V3). */
export default function Sidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const authUser = useAuthStore((state) => state.user)
  const setUser = useAuthStore((state) => state.setUser)
  const logout = useAuthStore((state) => state.logout)
  const preference = useThemeStore((state) => state.preference)
  const setPreference = useThemeStore((state) => state.setPreference)
  const { credits, isLoading: creditsLoading } = useCredits()

  const { data: me } = useQuery({ queryKey: ['currentUser'], queryFn: getCurrentUser, staleTime: 60000 })
  const user = me ?? authUser
  useEffect(() => {
    if (me) setUser(me)
  }, [me, setUser])

  const items = NAV_ITEMS.filter((item) => item.visible(user))
  const active = navItemFor(location.pathname)
  const badges = useNavBadges(user, items.some((i) => i.key === 'projets'))
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <aside className="sticky top-0 flex h-screen w-[250px] shrink-0 flex-col overflow-y-auto border-r border-border bg-canvas">
      <Link to="/dashboard" className="flex items-center border-b border-border px-5 pb-5 pt-6" aria-label="Jean Louis : accueil">
        <BrandLogo className="h-10 w-auto" />
      </Link>

      <nav className="flex flex-col gap-0.5 px-2 py-3" aria-label="Navigation principale">
        {items.map((item) => {
          if (item.separator) return <div key={item.key} className="mx-3 my-3 border-t border-border" />
          const Icon = item.icon
          const isActive = active?.key === item.key
          const count = item.key === 'jour' ? badges.jour : item.badge ? badges[item.badge] : 0
          return (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.end}
              aria-current={isActive ? 'page' : undefined}
              className={`relative flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-sm font-medium transition ${
                isActive ? 'bg-surface-active text-ink' : 'text-foreground-secondary hover:bg-surface-hover hover:text-ink'
              }`}
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
              {count > 0 && (
                <span className={`min-w-[22px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-semibold ${
                  item.key === 'jour' ? 'bg-red-500/15 text-red-400' : 'bg-brand text-white'
                }`}>
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </NavLink>
          )
        })}
      </nav>

      <div className="mx-3 mb-4 mt-auto flex flex-col gap-2 rounded-[12px] border border-border bg-surface-hover p-3 text-xs">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground-muted">Espace commercial</p>
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-ink">{fullName}</p>
          <p className="text-xs text-foreground-secondary">{roleLabel(user)}{user?.organization?.name ? ` · ${user.organization.name}` : ''}</p>
        </div>

        <div className="flex gap-0.5 rounded-full bg-surface-active p-[3px]" role="radiogroup" aria-label="Thème">
          {THEMES.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={preference === value}
              onClick={() => setPreference(value)}
              title={label}
              className={`flex min-w-0 flex-1 items-center justify-center gap-1 rounded-full px-1 py-1 text-[10px] font-medium transition ${
                preference === value ? 'bg-surface text-ink shadow-sm' : 'text-foreground-secondary hover:text-ink'
              }`}
            >
              <Icon className="h-3 w-3" />
              {label}
            </button>
          ))}
        </div>

        <Link to="/dashboard/compte/credits" className="mt-1 border-t border-border pt-2.5 text-ink">
          <span className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-foreground-secondary"><Zap className="h-3.5 w-3.5 text-brand-text" /> Crédits</span>
            <span className="font-semibold">{creditsLoading ? '…' : Number(credits).toLocaleString('fr-FR')}</span>
          </span>
          <span className="mt-1 block text-[11px] text-brand-text">Acheter des crédits →</span>
        </Link>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-1 flex items-center gap-1.5 text-[11px] text-foreground-muted transition hover:text-red-400"
        >
          <LogOut className="h-3.5 w-3.5" /> Déconnexion
        </button>
      </div>
    </aside>
  )
}
